import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useQuery, useMutation, ApolloError } from '@apollo/client';
import { ME } from '../graphql/queries';
import { LOGIN, LOGOUT, REGISTER, GOOGLE_AUTH } from '../graphql/mutations';

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  userType: string;
  profilePicture?: string;
  isVerified: boolean;
  phone?: string | null;
  phoneVerified: boolean;
  emailVerified: boolean;
  canPostAd: boolean;
  remainingListings: number;
  isStaff?: boolean | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: ApolloError | undefined;
  login: (username: string, password: string) => Promise<{ success: boolean; message: string; requiresVerification?: boolean; verificationType?: string; verificationEmail?: string }>;
  logout: () => Promise<void>;
  googleSignIn: (idToken: string) => Promise<{ success: boolean; message: string; isNewUser?: boolean }>;
  register: (input: RegisterInput) => Promise<{ success: boolean; message: string; requiresVerification?: boolean; verificationType?: string }>;
  refetchUser: () => void;
}

interface RegisterInput {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  userType: string;
  companyName?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  
  const { loading, error, refetch } = useQuery(ME, {
    fetchPolicy: 'network-only',
    onError: (err) => {
      console.error('Auth query error:', err);
      setUser(null);
    },
    onCompleted: (data) => {
      if (data?.me) {
        setUser(data.me);
      } else {
        setUser(null);
      }
    }
  });

  const [loginMutation] = useMutation(LOGIN);
  const [logoutMutation] = useMutation(LOGOUT);
  const [registerMutation] = useMutation(REGISTER);
  const [googleAuthMutation] = useMutation(GOOGLE_AUTH);

  const googleSignIn = async (idToken: string) => {
    try {
      const { data } = await googleAuthMutation({ variables: { idToken } });
      if (data?.googleAuth?.success) {
        await refetch();
      }
      return {
        success: data?.googleAuth?.success || false,
        message: data?.googleAuth?.message || 'Google sign-in failed',
        isNewUser: data?.googleAuth?.isNewUser,
      };
    } catch (error: any) {
      return { success: false, message: error.message || 'Google sign-in failed' };
    }
  };

  const login = async (username: string, password: string) => {
    try {
      const { data } = await loginMutation({
        variables: { username, password },
      });
      
      if (data?.login?.success) {
        await refetch();
      }
      
      return {
        success: data?.login?.success || false,
        message: data?.login?.message || 'Login failed',
        requiresVerification: data?.login?.requiresVerification,
        verificationType: data?.login?.verificationType,
        verificationEmail: data?.login?.verificationEmail,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Login failed',
      };
    }
  };

  const logout = async () => {
    try {
      await logoutMutation();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const register = async (input: RegisterInput) => {
    try {
      const { data } = await registerMutation({
        variables: { input },
      });
      
      return {
        success: data?.register?.success || false,
        message: data?.register?.message || 'Registration failed',
        requiresVerification: data?.register?.requiresVerification,
        verificationType: data?.register?.verificationType,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Registration failed',
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading: loading,
        error,
        login,
        logout,
        googleSignIn,
        register,
        refetchUser: refetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
